import React from "react";
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { FaRegQuestionCircle } from "react-icons/fa";
import { ProfitAndLossItems } from "../../contexts/constants";
import { useTranslation } from "react-i18next";

// レベルに応じたインデント（px）を取得
const getLevelIndent = (level: number): number => {
  switch (level) {
    case 1: return 0;
    case 2: return 6;
    case 3: return 12;
    case 4: return 16;
    default: return 0;
  }
};


/**
 * 年度別損益の項目説明ポップオーバー
 *
 * 使用例:
 * <ProfitLossExplanationPopover />
 */
export const ProfitLossExplanationPopover: React.FC<{ isGeneralPurpose?: boolean }> = ({ isGeneralPurpose }) => {
  const { t } = useTranslation();
  const p = "features.InvestmentScreening.Popover.profitLossExplanation";
  const qpLabel = isGeneralPurpose ? t(`${p}.qpLabelGeneral`) : t(`${p}.qpLabelNormal`);
  const qLabel = isGeneralPurpose ? t(`${p}.qLabelGeneral`) : t(`${p}.qLabelNormal`);
  const profitLossExplanationData = [
    { item: ProfitAndLossItems.totalSales, description: t(`${p}.totalOf2to5`), isBold: true },
    { item: ProfitAndLossItems.productSales, description: t(`${p}.screenInput`, { label: qpLabel }) },
    { item: ProfitAndLossItems.moldRecovery, description: t(`${p}.screenInput`, { label: qpLabel }) },
    { item: ProfitAndLossItems.devRecovery, description: t(`${p}.screenInput`, { label: qpLabel }) },
    { item: ProfitAndLossItems.equipmentRecovery, description: t(`${p}.screenInput`, { label: qpLabel }) },
    { item: ProfitAndLossItems.salesCost, description: t(`${p}.totalOf7to18`), isBold: true },
    { item: ProfitAndLossItems.materialCost, description: t(`${p}.materialCostFormula`, { qLabel }) },
    { item: ProfitAndLossItems.outsideCost, description: t(`${p}.outsideCostFormula`) },
    { item: ProfitAndLossItems.laborCost, description: t(`${p}.laborCostFormula`, { qLabel }) },
    { item: ProfitAndLossItems.domesticLaborCost, description: t(`${p}.expenseScreenInput`) },
    { item: ProfitAndLossItems.factoryExpenses, description: t(`${p}.totalOf12to16`) },
    { item: ProfitAndLossItems.overHeadCost, description: t(`${p}.overHeadCostFormula`, { qLabel }) },
    { item: ProfitAndLossItems.depreciation, description: t(`${p}.totalOf14to16`) },
    { item: ProfitAndLossItems.equipmentDepreciation, description: t(`${p}.equipDepreciationDesc`) },
    { item: ProfitAndLossItems.factoryCost, description: t(`${p}.expenseScreenInput`) },
    { item: ProfitAndLossItems.otherEquipmentCost, description: t(`${p}.expenseScreenInput`) },
    { item: ProfitAndLossItems.domesticFactoryExpenses, description: t(`${p}.expenseScreenInput`) },
    { item: ProfitAndLossItems.scrapCost, description: t(`${p}.scrapCostFormula`, { qLabel }) },
    { item: ProfitAndLossItems.sgaExpenses, description: t(`${p}.totalOf20to24`), isBold: true },
    { item: ProfitAndLossItems.appliedLaborCost, description: t(`${p}.expenseScreenInput`) },
    { item: ProfitAndLossItems.domesticSgaCost, description: t(`${p}.expenseScreenInput`) },
    { item: ProfitAndLossItems.salesManagementCost, description: t(`${p}.expenseScreenInput`) },
    { item: ProfitAndLossItems.devCost, description: t(`${p}.expenseScreenInput`) },
    { item: ProfitAndLossItems.royalty, description: t(`${p}.expenseScreenInput`) },
    { item: ProfitAndLossItems.otherCost, description: t(`${p}.expenseScreenInput`), isBold: true },
    { item: ProfitAndLossItems.operatingProfit, description: t(`${p}.operatingProfitFormula`), isBold: true },
    { item: ProfitAndLossItems.operatingProfitRate, description: t(`${p}.operatingProfitRateFormula`), isBold: true },
    { item: ProfitAndLossItems.realOperatingProfitRate, description: t(`${p}.realOperatingProfitRateFormula`), isBold: true },
  ];

  return (
    <Popover placement="right">
      <PopoverTrigger>
        <button style={{ background: 'none', border: 'none', padding: 0 }}>
          <FaRegQuestionCircle size="30px" cursor="pointer" />
        </button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="1000px" maxH="600px" overflowY="auto" bgColor="#fffff0">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontWeight="bold">{t(`${p}.title`)}</PopoverHeader>
          <PopoverBody>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th w="60px">{t('common.label.no')}</Th>
                  <Th w="300px">{t('common.label.item')}</Th>
                  <Th>{t('common.label.description')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {profitLossExplanationData.map((row, index) => (
                  <Tr key={index}>
                    <Td>{index + 1}</Td>
                    <Td
                      fontWeight={row.isBold ? "bold" : "normal"}
                      pl={getLevelIndent(row.item.level)}
                    >
                      {row.item.name}
                    </Td>
                    <Td>{row.description}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
